import { useQueryClient, useMutation } from "@tanstack/react-query";
import { useToast } from "@repo/ui";
import { fileApi } from "~/api";
import type { TaskManagerDTO } from "~/types/api-custom";
import { getTasksQueryKey, TASK_TOAST_MESSAGES } from "../task-dialog/task-constants";

type MutationVariables = {
  taskId: number;
  active: boolean;
  originalActive?: boolean;
  onSuccess?: () => void;
};

export const useUpdateTaskStatus = () => {
  const { showToast } = useToast();
  const queryClient = useQueryClient();

  const tasksQueryKey = getTasksQueryKey();

  const { mutateAsync: mutateTaskStatus, isPending } = useMutation({
    mutationFn: async ({ taskId, active, originalActive }: MutationVariables) => {
      if (!active && originalActive) {
        const completeResponse = await fileApi.client.PATCH("/api/Task/{taskId}/complete", {
          params: { path: { taskId: taskId } },
        });

        if (completeResponse.error) {
          throw new Error(`Failed to complete task: ${completeResponse.error.toString()}`);
        }

        return { id: taskId, active };
      }

      return { id: taskId, active };
    },
    onMutate: async ({ taskId, active }) => {
      await queryClient.cancelQueries({ queryKey: tasksQueryKey });
      const previousTasks = queryClient.getQueryData<TaskManagerDTO[]>(tasksQueryKey);
      const originalTask = previousTasks?.find((t) => t.id === taskId);

      queryClient.setQueryData<TaskManagerDTO[]>(tasksQueryKey, (old) =>
        Array.isArray(old)
          ? old.map((task) =>
              task.id === taskId
                ? {
                    ...task,
                    active,
                  }
                : task
            )
          : old
      );

      return { previousTasks, originalTask };
    },
    onError: (err, _vars, context) => {
      if (context?.previousTasks) {
        queryClient.setQueryData(tasksQueryKey, context.previousTasks);
      }
      if (err instanceof Error) {
        showToast("error", "Error", TASK_TOAST_MESSAGES.STATUS_UPDATE_ERROR);
      }
    },
    onSuccess: (_data, variables) => {
      const message = !variables.active
        ? TASK_TOAST_MESSAGES.STATUS_COMPLETE_SUCCESS
        : TASK_TOAST_MESSAGES.STATUS_UPDATE_SUCCESS;
      showToast("success", "Success", message);
      variables.onSuccess?.();
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: tasksQueryKey });
    },
  });

  const handleUpdateStatus = async (taskId: number, active: boolean, onSuccess?: () => void) => {
    const tasks = queryClient.getQueryData<TaskManagerDTO[]>(tasksQueryKey);
    const originalTask = tasks?.find((t) => t.id === taskId);
    const originalActive = originalTask?.active;

    await mutateTaskStatus({ taskId, active, originalActive, onSuccess });
  };

  return { handleUpdateStatus, isPending };
};
